package com.karyadina.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.Image
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForwardIos
import androidx.compose.material.icons.filled.OpenInNew
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.common.generated.resources.logo
import com.karyadina.ui.theme.StudyIcons
import com.karyadina.ui.theme.StudyStrings
import com.karyadina.ui.theme.StudyTheme
import org.jetbrains.compose.resources.stringResource
import com.karyadina.ui.components.DonationCard

// ──────────────────────────────────────────────────────────────
// Open-source libraries used in this project
// ──────────────────────────────────────────────────────────────
private data class OssLib(
    val name: String,
    val description: String,
    val license: String,
    val url: String
)

private val ossLibraries = listOf(
    OssLib(
        "Jetpack Compose / Compose Multiplatform",
        "Modern declarative UI toolkit by JetBrains & Google",
        "Apache 2.0",
        "https://github.com/JetBrains/compose-multiplatform"
    ),
    OssLib(
        "Kotlin Coroutines",
        "Asynchronous programming library for Kotlin",
        "Apache 2.0",
        "https://github.com/Kotlin/kotlinx.coroutines"
    ),
    OssLib(
        "Kotlin Serialization",
        "Multiplatform Kotlin serialization runtime",
        "Apache 2.0",
        "https://github.com/Kotlin/kotlinx.serialization"
    ),
    OssLib(
        "kotlinx-datetime",
        "Multiplatform date/time library for Kotlin",
        "Apache 2.0",
        "https://github.com/Kotlin/kotlinx-datetime"
    ),
    OssLib(
        "kotlinx-io",
        "Multiplatform I/O library for Kotlin",
        "Apache 2.0",
        "https://github.com/Kotlin/kotlinx-io"
    ),
    OssLib(
        "Ktor",
        "Asynchronous HTTP client for Kotlin Multiplatform",
        "Apache 2.0",
        "https://github.com/ktorio/ktor"
    ),
    OssLib(
        "Koin",
        "Pragmatic lightweight dependency injection for Kotlin",
        "Apache 2.0",
        "https://github.com/InsertKoinIO/koin"
    ),
    OssLib(
        "Multiplatform Settings",
        "Key-value storage library for Kotlin Multiplatform",
        "Apache 2.0",
        "https://github.com/russhwolf/multiplatform-settings"
    ),
    OssLib(
        "Lifecycle ViewModel Compose",
        "Jetpack ViewModel integration for Compose",
        "Apache 2.0",
        "https://developer.android.com/jetpack/androidx/releases/lifecycle"
    ),
    OssLib(
        "iText 7 Core",
        "PDF creation and manipulation library",
        "AGPL 3.0",
        "https://github.com/itext/itext7"
    ),
    OssLib(
        "CameraX",
        "Jetpack camera API (camera2, lifecycle, view)",
        "Apache 2.0",
        "https://developer.android.com/training/camerax"
    ),
    OssLib(
        "ML Kit Text Recognition",
        "On-device text recognition (Latin & Devanagari)",
        "Apache 2.0",
        "https://developers.google.com/ml-kit/vision/text-recognition"
    ),
    OssLib(
        "AndroidX ExifInterface",
        "Reading EXIF metadata from image files",
        "Apache 2.0",
        "https://developer.android.com/jetpack/androidx/releases/exifinterface"
    ),
    OssLib(
        "Material Components for Android",
        "Material Design components for Android views",
        "Apache 2.0",
        "https://github.com/material-components/material-components-android"
    ),
    OssLib(
        "Material Icons Extended",
        "Extended Material Design icon set for Compose",
        "Apache 2.0",
        "https://github.com/material-components/material-components-android"
    ),
)

// ──────────────────────────────────────────────────────────────
// AboutScreen
// ──────────────────────────────────────────────────────────────
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AboutScreen(
    onBackClick: () -> Unit,
    onPrivacyPolicyClick: () -> Unit = {},          // https://kridaprema.com/karyadina/privacy-policy
    onExploreWebClick: () -> Unit = {},             // https://kridaprema.com/karyadina
    onPrivacyWebClick: () -> Unit = {},             // https://kridaprema.com/karyadina/privacy-policy
    onOpenUrl: (String) -> Unit = {}               // generic URL opener for OSS links
) {
    var showLicenses by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(colors = StudyTheme.BannerGradient)
            )
    ) {
        // 1. Watermark Logo (Background)
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            Image(
                painter = org.jetbrains.compose.resources.painterResource(
                    com.karyadina.common.generated.resources.Res.drawable.logo
                ),
                contentDescription = null,
                modifier = Modifier
                    .fillMaxSize()
                    .alpha(0.12f),
                contentScale = androidx.compose.ui.layout.ContentScale.Crop,
                colorFilter = androidx.compose.ui.graphics.ColorFilter.tint(
                    MaterialTheme.colorScheme.onSurface
                )
            )
        }

        // 2. Main Content
        Column(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
                .verticalScroll(rememberScrollState())
                .padding(bottom = 32.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Top Bar
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 8.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onBackClick) {
                    Icon(
                        StudyIcons.ArrowBack,
                        stringResource(StudyStrings.Back),
                        tint = MaterialTheme.colorScheme.onSurface
                    )
                }
                Spacer(modifier = Modifier.weight(1f))
            }

            Spacer(modifier = Modifier.height(24.dp))

            // Logo & Brand
            Surface(
                modifier = Modifier.size(120.dp),
                shape = CircleShape,
                color = MaterialTheme.colorScheme.surface,
                shadowElevation = 12.dp,
                tonalElevation = 6.dp
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Image(
                        painter = org.jetbrains.compose.resources.painterResource(
                            com.karyadina.common.generated.resources.Res.drawable.logo
                        ),
                        contentDescription = "App Logo",
                        modifier = Modifier.size(64.dp)
                    )
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            Text(
                stringResource(StudyStrings.AppName),
                style = MaterialTheme.typography.displaySmall,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                stringResource(StudyStrings.CompanyCredit),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.4f),
                modifier = Modifier.alpha(0.6f)
            )

            Spacer(modifier = Modifier.height(48.dp))

            // Version Info Card
            ElevatedCard(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp),
                colors = CardDefaults.elevatedCardColors(
                    containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.9f)
                ),
                shape = MaterialTheme.shapes.large
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(20.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text(
                        stringResource(StudyStrings.VersionLabel),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.primary
                    )
                }
            }

            Spacer(modifier = Modifier.height(24.dp))
            
            // Donation / Support Card
            Box(modifier = Modifier.padding(horizontal = 24.dp)) {
                DonationCard()
            }

            Spacer(modifier = Modifier.height(32.dp))

            // Links section
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Privacy Policy — points to web URL (in-app screen not yet implemented)
                SettingsItem(
                    icon = StudyIcons.Privacy,
                    title = stringResource(StudyStrings.PrivacyPolicyTitle),
                    subtitle = stringResource(StudyStrings.PrivacyWebLinkDesc), // "kridaprema.com/karyadina/privacy-policy"
                    onClick = onPrivacyPolicyClick
                )

                // Open-source licenses (expandable)
                SettingsItem(
                    icon = StudyIcons.Licenses,
                    title = stringResource(StudyStrings.LicensesTitle),
                    subtitle = stringResource(StudyStrings.LicensesDesc),
                    onClick = { showLicenses = true }
                )

                // Explore on Web
                SettingsItem(
                    icon = StudyIcons.ExploreWeb,
                    title = stringResource(StudyStrings.ExploreWebTitle),
                    subtitle = stringResource(StudyStrings.ExploreWebDesc),
                    onClick = onExploreWebClick
                )
            }
        }
    }

    // OSS Licenses Bottom Sheet
    if (showLicenses) {
        OssLicensesSheet(
            onDismiss = { showLicenses = false },
            onOpenUrl = onOpenUrl
        )
    }
}

// ──────────────────────────────────────────────────────────────
// OSS Licenses Bottom Sheet
// ──────────────────────────────────────────────────────────────
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun OssLicensesSheet(
    onDismiss: () -> Unit,
    onOpenUrl: (String) -> Unit
) {
    val sheetState = rememberModalBottomSheetState()

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        containerColor = MaterialTheme.colorScheme.surface,
        shape = MaterialTheme.shapes.extraLarge
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())       // ← makes library list scrollable
                .navigationBarsPadding()                     // ← respects gesture nav bar
                .padding(horizontal = 20.dp)
                .padding(bottom = 32.dp)
        ) {
            // Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    imageVector = StudyIcons.Licenses,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(22.dp)
                )
                Spacer(Modifier.width(10.dp))
                Text(
                    "Open Source Libraries",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
            }
            Text(
                "This app is built with the following open-source libraries. Tap any to view its source.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(bottom = 16.dp)
            )

            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.4f))
            Spacer(Modifier.height(8.dp))

            // Library list
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                ossLibraries.forEach { lib ->
                    OssLibRow(lib = lib, onOpenUrl = onOpenUrl)
                }
            }
        }
    }
}

@Composable
private fun OssLibRow(lib: OssLib, onOpenUrl: (String) -> Unit) {
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onOpenUrl(lib.url) },
        color = MaterialTheme.colorScheme.surfaceContainerLow,
        shape = MaterialTheme.shapes.medium
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = lib.name,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = lib.description,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Text(
                    text = lib.license,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.primary,
                    fontWeight = FontWeight.Medium
                )
            }
            Icon(
                imageVector = Icons.Filled.OpenInNew,
                contentDescription = "Open",
                tint = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.35f),
                modifier = Modifier.size(16.dp)
            )
        }
    }
}

// ──────────────────────────────────────────────────────────────
// Settings row item
// ──────────────────────────────────────────────────────────────
@Composable
private fun SettingsItem(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    title: String,
    subtitle: String,
    onClick: () -> Unit
) {
    Surface(
        onClick = onClick,
        color = MaterialTheme.colorScheme.surface.copy(alpha = 0.6f),
        shape = MaterialTheme.shapes.medium,
        border = androidx.compose.foundation.BorderStroke(
            1.dp,
            MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f)
        )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                color = MaterialTheme.colorScheme.primaryContainer,
                shape = CircleShape,
                modifier = Modifier.size(40.dp)
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onPrimaryContainer,
                        modifier = Modifier.size(20.dp)
                    )
                }
            }
            Spacer(modifier = Modifier.width(16.dp))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Icon(
                imageVector = Icons.AutoMirrored.Filled.ArrowForwardIos,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f),
                modifier = Modifier.size(16.dp)
            )
        }
    }
}
